import { Spacing, Typography } from '@persona-id/design-tokens';
import { TransferOutIcon } from '@persona-id/design-tokens/icons';
import IconPlus from '@persona-id/design-tokens/icons/PlusIcon';
import { useState } from 'react';
import { FileRejection } from 'react-dropzone';
import { Controller, UseFormReturn } from 'react-hook-form';
import { FormError } from 'shared/components/FormControl';
import { COLORS, FONT_SIZES } from 'shared/styles/variables';
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';

import CopyableField from 'dashboard/components/CopyableField';
import OauthPopup from 'dashboard/components/OauthPopup';
import ReactSelect, { OptionType } from 'dashboard/components/ReactSelect';
import FileUpload from 'dashboard/components-lib/FileUpload';
import { FileWithPreview } from 'dashboard/components-lib/FileUpload/types';
import {
  FormNewDataSourceData,
  NewDataSourceType,
  PageBody,
  PageButtonContainer,
} from 'dashboard/forms/FormNewDataSource/types';
import { DATA_SOURCE_TYPES, RelationalDataSource } from 'dashboard/lib/constants/data-sources';
import { GoogleSheetsFilesResponse, S3Config } from 'dashboard/models/external-integration';
import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import Radio from 'design-system/components/Radio';
import RadioGroup from 'design-system/components/RadioGroup';
import Spacer from 'design-system/components/Spacer';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';

const InputLabel = styled('p')`
  ${Typography.TextMedium};
  padding-bottom: ${Spacing[1]};
`;

const SaveButton = styled(Button)`
  width: fit-content;
  float: right;
`;

const ConnectButton = styled(Button)`
  width: fit-content;
  float: right;
`;

const ContinueButtonContainer = styled.div`
  width: 100%;
`;

const FileUploadLabelContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Hint = styled('p')`
  font-size: ${FONT_SIZES.small};
  color: ${COLORS.gray50};
`;

const ErrorMessage = styled('div')`
  color: ${({ theme }) => theme.color.variants.danger.default};
  font-size: ${({ theme }) => theme.text.paragraph.mdBold.fontSize};
`;

interface AddDataSourcePageProps {
  form: UseFormReturn<FormNewDataSourceData>;
  s3Config: S3Config | null;
  onS3Connect: () => Promise<void>;
  getAllGoogleSheets: (code: string, redirectUrl: string) => Promise<GoogleSheetsFilesResponse>;
  dataSourceType: NewDataSourceType | null;
  onSubmit: () => void;
}

declare const GOOGLE_OAUTH_CLIENT_ID: string;
const OAUTH_SCOPE = [
  'https://www.googleapis.com/auth/userinfo.profile',
  'https://www.googleapis.com/auth/spreadsheets.readonly',
  'https://www.googleapis.com/auth/drive.readonly',
];
const GOOGLE_OAUTH_REDIRECT_URL = `${location.origin}/oauth_callback/`;

const AddDataSourcePage = ({
  form,
  s3Config,
  onS3Connect,
  getAllGoogleSheets,
  dataSourceType,
  onSubmit,
}: AddDataSourcePageProps) => {
  const [showErrorMessage, { enable: openErrorMessage, disable: hideErrorMessage }] =
    useToggle(false);
  const [spreadsheets, setSpreadsheets] = useState<OptionType[]>();

  const {
    control,
    clearErrors,
    formState: { errors, isDirty, isSubmitting, isValid },
    setError,
    setValue,
    register,
    watch,
  } = form;

  const onFileRejection = (fileRejections: FileRejection[]) => {
    const errorMessages = [];
    for (const error of fileRejections[0].errors) {
      if (error.code === 'file-too-large') {
        errorMessages.push('File too large. Max file size is 200MB.');
      } else if (error.code === 'file-invalid-type') {
        errorMessages.push('Invalid file type. File must be a CSV.');
      }
    }
    setError('file', { type: 'custom', message: errorMessages.join('\n') });
  };

  const includeS3DataSource = useFeatureFlag('dashboard:graph-s3-datasource') && s3Config;
  const includeRedshiftOption = useFeatureFlag('dashboard:graph-redshift-importer');
  const includeGoogleSheetsOption = useFeatureFlag('dashboard:graph-google-sheets-importer');

  const filterDisabledDataSource = (key: any) => {
    const isRedshift = key === ApiModel.ExternalIntegrationDataSourceRedshift;
    const isS3 = key === ApiModel.ExternalIntegrationDataSourceS3;
    const isGoogleSheetsOption = key === ApiModel.ExternalIntegrationDataSourceGoogleSheets;

    // remove data source from options if flag is disabled
    return (
      (includeRedshiftOption || !isRedshift) &&
      (includeS3DataSource || !isS3) &&
      (includeGoogleSheetsOption || !isGoogleSheetsOption)
    );
  };

  const options = Object.keys(DATA_SOURCE_TYPES)
    .filter(filterDisabledDataSource)
    .sort((a, b) => a.localeCompare(b, 'en', { sensitivity: 'base' }))
    .map((key) => ({
      label: DATA_SOURCE_TYPES[key as keyof typeof DATA_SOURCE_TYPES],
      value: key,
    }));

  const onFileChange = (
    file: FileWithPreview | null,
    onChange: (event: FileWithPreview | null) => void,
  ) => {
    // opens the uploaded csv file for quick csv validation
    //  - no duplicate column names
    //  - no empty string as a column name
    const reader = new FileReader();
    reader.onload = function (e) {
      const errorMessages: string[] = [];
      const decoder = new TextDecoder();

      if (e.target == null) {
        setError('file', { type: 'custom', message: 'Invalid File' });
        return;
      }
      // we read the file as an array buffer, so e.target.result should not be a string
      const contents = decoder.decode(e.target.result as ArrayBuffer);

      const firstNewLineIndex = contents.indexOf('\n');
      const headerRow = firstNewLineIndex >= 0 ? contents.slice(0, firstNewLineIndex) : contents;
      // this validiation assumes headerRow contains no \n or , inside a quote
      // which I think is reasonable for now
      const columnNames: Set<string> = new Set();
      let emptyStringFound = false;
      for (const col of headerRow.split(',')) {
        const colName = col.trim();
        if (colName.length === 0) {
          if (!emptyStringFound) {
            errorMessages.push('Column Name in csv cannot be an empty string. ');
            emptyStringFound = true;
          }
        } else if (columnNames.has(colName)) {
          errorMessages.push(`Duplicate Column '${colName}' found in csv. `);
        }
        columnNames.add(colName);
      }

      if (errorMessages.length > 0) {
        setError('file', { type: 'custom', message: errorMessages.join('\n') });
      } else {
        clearErrors('file');
        onChange(file);
      }
    };
    // file is null upon removing an uploaded file
    if (file == null) {
      clearErrors('file');
      onChange(file);
    } else {
      // generously assume the header does not exceed 10kB
      const CHUNK_SIZE = 10 * 1024;
      reader.readAsArrayBuffer(file.slice(0, CHUNK_SIZE));
    }
  };

  const getGoogleOAuthURL = () => {
    const params = new URLSearchParams({
      redirect_uri: GOOGLE_OAUTH_REDIRECT_URL,
      client_id: GOOGLE_OAUTH_CLIENT_ID,
      access_type: 'offline',
      response_type: 'code',
      prompt: 'consent',
      scope: OAUTH_SCOPE.join(' '),
    } as Record<string, string>);
    return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  };

  const onCode = async (code: string) => {
    hideErrorMessage();
    const refreshTokenResponse = await getAllGoogleSheets(code, GOOGLE_OAUTH_REDIRECT_URL);
    setValue('refreshToken', refreshTokenResponse.token);

    const filesToId: OptionType[] = refreshTokenResponse.files.map((file: Array<string>) => {
      return {
        value: file[1],
        label: file[0],
      };
    });
    setSpreadsheets(filesToId);
  };

  const getDefaultPort = (): string =>
    watch('type') === ApiModel.ExternalIntegrationDataSourceRedshift ? '5439' : '3306';

  return (
    <div>
      <PageBody>
        {dataSourceType == null ? (
          <>
            <h3>Credentials</h3>
            To connect the external data source, enter your credentials required to access this data
            source.
            <Spacer height={3} />
            <InputLabel>Data source type</InputLabel>
            <RadioGroup>
              {options.map((option) => {
                return (
                  <Radio
                    key={option.value}
                    label={option.label}
                    {...register('type', {
                      required: 'Type is required',
                      onChange: () => setValue('port', getDefaultPort()),
                    })}
                    disabled={
                      ![
                        ApiModel.ExternalIntegrationDataSourceCsv,
                        ApiModel.ExternalIntegrationDataSourceGoogleSheets,
                        ApiModel.ExternalIntegrationDataSourceMysql,
                        ApiModel.ExternalIntegrationDataSourceSnowflake,
                        ApiModel.ExternalIntegrationDataSourceRedshift,
                        ApiModel.ExternalIntegrationDataSourceS3,
                      ].includes(option.value as ApiModel)
                    }
                    value={option.value}
                  />
                );
              })}
            </RadioGroup>
            {errors['type'] && <FormError>{errors['type'].message}</FormError>}
          </>
        ) : (
          <>
            <InputLabel>Type</InputLabel>
            <p>{DATA_SOURCE_TYPES[dataSourceType]}</p>
          </>
        )}
        <Spacer height={3} />

        <InputLabel>Name</InputLabel>
        <Controller
          control={control}
          name='name'
          rules={{
            validate: (val) => {
              if (val == null || val.trim() === '') {
                return 'Name is required';
              }
            },
          }}
          render={({ field: { onChange, value } }) => {
            return (
              <Input value={value || ''} onChange={onChange} placeholder='Enter data source name' />
            );
          }}
        />
        {errors['name'] && <FormError>{errors['name'].message}</FormError>}
        <Spacer height={3} />

        {watch('type') === ApiModel.ExternalIntegrationDataSourceCsv && (
          <>
            <Controller
              control={control}
              name='file'
              rules={{ required: 'File is required' }}
              render={({ field: { onChange, value } }) => {
                return (
                  <FileUpload
                    accept={['text/csv']}
                    label={
                      <FileUploadLabelContainer>
                        <p>Add a CSV of static data</p>
                        <Spacer height={3} />
                        <Button>
                          <TransferOutIcon /> Upload CSV
                        </Button>
                      </FileUploadLabelContainer>
                    }
                    maxSize={200 * 1024 * 1024} // 200MB; NB: update error message too
                    value={value}
                    onChange={(file) => {
                      onFileChange(file, onChange);
                      if (!watch('name')) {
                        setValue('name', file?.name || '');
                      }
                    }}
                    onReject={onFileRejection}
                  />
                );
              }}
            />
            {(errors as any)['file'] && <FormError>{(errors as any)['file'].message}</FormError>}
          </>
        )}
        {RelationalDataSource.includes(watch('type')) && (
          <>
            <InputLabel>Hostname</InputLabel>
            <Controller
              control={control}
              name='host'
              rules={{ required: 'Hostname is required' }}
              render={({ field: { onChange, value } }) => {
                return (
                  <Input
                    value={value || ''}
                    onChange={onChange}
                    placeholder='Enter database hostname'
                  />
                );
              }}
            />
            {(errors as any)['host'] && <FormError>{(errors as any)['host'].message}</FormError>}
            <Spacer height={3} />
            <InputLabel>Port</InputLabel>
            <Controller
              control={control}
              name='port'
              rules={{ required: 'Port is required' }}
              render={({ field: { onChange, value } }) => {
                return (
                  <Input
                    value={value || getDefaultPort()}
                    onChange={onChange}
                    placeholder='Enter database port'
                  />
                );
              }}
            />
            <Spacer height={3} />
            <InputLabel>Username</InputLabel>
            <Controller
              control={control}
              name='user'
              rules={{ required: 'Username is required' }}
              render={({ field: { onChange, value } }) => {
                return (
                  <Input
                    value={value || ''}
                    onChange={onChange}
                    placeholder='Enter database username'
                  />
                );
              }}
            />
            {(errors as any)['user'] && <FormError>{(errors as any)['user'].message}</FormError>}
            <Spacer height={3} />
            <InputLabel>Password</InputLabel>
            <Controller
              control={control}
              name='password'
              rules={{ required: 'Password is required' }}
              render={({ field: { onChange, value } }) => {
                return (
                  <Input
                    value={value || ''}
                    onChange={onChange}
                    placeholder='Enter database password'
                    type='password'
                  />
                );
              }}
            />
            {(errors as any)['password'] && (
              <FormError>{(errors as any)['password'].message}</FormError>
            )}
            <Spacer height={3} />
            <InputLabel>Database Name</InputLabel>
            <Controller
              control={control}
              name='database'
              rules={{ required: 'Database is required' }}
              render={({ field: { onChange, value } }) => {
                return (
                  <Input
                    value={value || ''}
                    onChange={onChange}
                    placeholder='Enter database name'
                  />
                );
              }}
            />
            {(errors as any)['database'] && (
              <FormError>{(errors as any)['database'].message}</FormError>
            )}

            {watch('type') === ApiModel.ExternalIntegrationDataSourceRedshift && (
              <>
                <Spacer height={3} />
                <InputLabel>Schema</InputLabel>
                <Controller
                  control={control}
                  name='schema'
                  rules={{ required: 'Schema is required' }}
                  render={({ field: { onChange, value } }) => {
                    return (
                      <Input value={value || ''} onChange={onChange} placeholder='Enter schema' />
                    );
                  }}
                />
                {(errors as any)['schema'] && (
                  <FormError>{(errors as any)['schema'].message}</FormError>
                )}
              </>
            )}
          </>
        )}
        {watch('type') === ApiModel.ExternalIntegrationDataSourceSnowflake && (
          <>
            <InputLabel>Account</InputLabel>
            <Controller
              control={control}
              name='account'
              rules={{ required: 'Account is required' }}
              render={({ field: { onChange, value } }) => {
                return (
                  <Input
                    value={value || ''}
                    onChange={onChange}
                    placeholder='Enter Snowflake account name'
                  />
                );
              }}
            />
            {(errors as any)['account'] && (
              <FormError>{(errors as any)['account'].message}</FormError>
            )}
            <Spacer height={3} />
            <InputLabel>Username</InputLabel>
            <Controller
              control={control}
              name='username'
              rules={{ required: 'Username is required' }}
              render={({ field: { onChange, value } }) => {
                return (
                  <Input
                    value={value || ''}
                    onChange={onChange}
                    placeholder='Enter database username'
                  />
                );
              }}
            />
            {(errors as any)['username'] && (
              <FormError>{(errors as any)['username'].message}</FormError>
            )}
            <Spacer height={3} />
            <InputLabel>Password</InputLabel>
            <Controller
              control={control}
              name='password'
              rules={{ required: 'Password is required' }}
              render={({ field: { onChange, value } }) => {
                return (
                  <Input
                    value={value || ''}
                    onChange={onChange}
                    placeholder='Enter database password'
                    type='password'
                  />
                );
              }}
            />
            {(errors as any)['password'] && (
              <FormError>{(errors as any)['password'].message}</FormError>
            )}
            <Spacer height={3} />
            <InputLabel>Warehouse</InputLabel>
            <Controller
              control={control}
              name='warehouse'
              rules={{ required: 'Warehouse is required' }}
              render={({ field: { onChange, value } }) => {
                return (
                  <Input value={value || ''} onChange={onChange} placeholder='Enter warehouse' />
                );
              }}
            />
            {(errors as any)['warehouse'] && (
              <FormError>{(errors as any)['warehouse'].message}</FormError>
            )}
            <Spacer height={3} />
            <InputLabel>Database Name</InputLabel>
            <Controller
              control={control}
              name='database'
              rules={{ required: 'Database Name is required' }}
              render={({ field: { onChange, value } }) => {
                return (
                  <Input
                    value={value || ''}
                    onChange={onChange}
                    placeholder='Enter database name'
                  />
                );
              }}
            />
            {(errors as any)['database'] && (
              <FormError>{(errors as any)['database'].message}</FormError>
            )}
            <Spacer height={3} />
            <InputLabel>Schema</InputLabel>
            <Controller
              control={control}
              name='schema'
              rules={{ required: 'Schema is required' }}
              render={({ field: { onChange, value } }) => {
                return <Input value={value || ''} onChange={onChange} placeholder='Enter schema' />;
              }}
            />
            {(errors as any)['schema'] && (
              <FormError>{(errors as any)['schema'].message}</FormError>
            )}
          </>
        )}
        {watch('type') === ApiModel.ExternalIntegrationDataSourceS3 && s3Config && (
          <>
            <InputLabel>AWS Account ID</InputLabel>
            <CopyableField value={s3Config.persona_aws_account_id} />
            <Spacer height={3} />
            <InputLabel>External ID</InputLabel>
            <Controller
              control={control}
              name='credentials.externalId'
              rules={{
                required: 'External Id is required',
                // TODO: (bjon) We want to encourage our customers to use meaningful external ids. It would be a security
                // risk if their external id is easily guessable to other customers. For now, we will just enforce that
                // the external id is not too short. Ideally, we also enforce that the external id is unique across all of its
                // other s3 datasources, but I am punting this as we dont add credentials to the datasource serializer. Besides, we already
                // enforce this on the backend.
                minLength: {
                  value: 12,
                  message: 'External ID must be at least 12 characters',
                },
              }}
              defaultValue={uuid()}
              render={({ field: { onChange, value } }) => {
                return (
                  <CopyableField
                    data-test='form-new-data-source__add-data-source-page__s3-external-id-field'
                    value={value}
                    onChange={onChange}
                    editable
                  />
                );
              }}
            />
            {(errors as any)['credentials'] && (errors as any)['credentials']['externalId'] && (
              <FormError>{(errors as any)['credentials']['externalId'].message}</FormError>
            )}
            <Spacer height={3} />
            <InputLabel>ARN</InputLabel>
            <Controller
              control={control}
              name='credentials.arn'
              rules={{ required: 'ARN is required' }}
              render={({ field: { onChange, value } }) => {
                return <Input value={value || ''} onChange={onChange} placeholder='Enter ARN' />;
              }}
            />
            {(errors as any)['credentials'] && (errors as any)['credentials']['arn'] && (
              <FormError>{(errors as any)['credentials']['arn'].message}</FormError>
            )}
            {(errors as any)['credentials'] && (
              <FormError>{(errors as any)['credentials'].message}</FormError>
            )}
            <Spacer height={3} />
            <InputLabel>Region</InputLabel>
            <Controller
              control={control}
              name='region'
              rules={{ required: 'Region is required' }}
              render={({ field: { onChange, value } }) => {
                return (
                  <ReactSelect
                    value={value}
                    options={s3Config.available_regions}
                    onChange={onChange}
                    placeholder='Choose Region'
                    portal={true}
                  />
                );
              }}
            />
            <Spacer height={3} />
            <InputLabel>Bucket</InputLabel>
            <Controller
              control={control}
              name='bucket'
              rules={{ required: 'Bucket is required' }}
              render={({ field: { onChange, value } }) => {
                return (
                  <Input value={value || ''} onChange={onChange} placeholder='Enter Bucket Name' />
                );
              }}
            />
            {(errors as any)['bucket'] && (
              <FormError>{(errors as any)['bucket'].message}</FormError>
            )}
            <Spacer height={3} />
            <InputLabel>CSV Path Prefix</InputLabel>
            <Controller
              control={control}
              name='csvPathPrefix'
              render={({ field: { onChange, value } }) => {
                return (
                  <Input
                    value={value || ''}
                    onChange={onChange}
                    placeholder='Enter CSV Data Path Prefix'
                  />
                );
              }}
            />
            {(errors as any)['csvPathPrefix'] && (
              <FormError>{(errors as any)['csvPathPrefix'].message}</FormError>
            )}
          </>
        )}
        {watch('type') === ApiModel.ExternalIntegrationDataSourceGoogleSheets && (
          <>
            <OauthPopup url={getGoogleOAuthURL()} onCode={onCode} onError={openErrorMessage}>
              <Button
                width='auto'
                aria-label='add button'
                variant='basic'
                prefixIcon={<IconPlus />}
              >
                Log in to Google Sheets
              </Button>
            </OauthPopup>
            <ErrorMessage hidden={!showErrorMessage}>Authetication Failed</ErrorMessage>
            <Spacer height={1} />
            <Hint>
              Limited access is required to import using Google Sheets. Your credentials will be
              encrypted and can be revoked anytime
            </Hint>
            <Spacer height={3} />
            <InputLabel>Spreadsheet</InputLabel>
            <Controller
              control={control}
              name='spreadsheetId'
              rules={{ required: 'Spreadsheet is required' }}
              render={({ field: { onChange, value } }) => {
                return (
                  <ReactSelect
                    value={value || ''}
                    options={spreadsheets ?? []}
                    onChange={onChange}
                    placeholder={
                      spreadsheets ? 'Choose spreadsheet' : 'Log in to Google to view spreadsheets'
                    }
                    portal={true}
                    disabled={!spreadsheets}
                  />
                );
              }}
            />
          </>
        )}
      </PageBody>
      <PageButtonContainer>
        <ContinueButtonContainer>
          {watch('type') === ApiModel.ExternalIntegrationDataSourceS3 ? (
            <ConnectButton
              disabled={isSubmitting || !isDirty || !isValid}
              isPending={isSubmitting}
              id='form-new-data-source__add-data-source-page__s3-connect-button'
              onClick={onS3Connect}
            >
              Connect & Add
            </ConnectButton>
          ) : (
            <SaveButton
              id='form-new-data-source__add-data-source-page__save-button'
              type='button'
              disabled={isSubmitting || !isDirty || !isValid}
              isPending={isSubmitting}
              onClick={onSubmit}
            >
              Create
            </SaveButton>
          )}
        </ContinueButtonContainer>
      </PageButtonContainer>
    </div>
  );
};

export default AddDataSourcePage;
